import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/feature/SEOHead";
import {
  heroData,
  featuresData,
  categoriesData,
  hotArticlesData,
  faqData,
  downloadStepsData,
} from "@/mocks/homeData";

const homepageSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "开云体育",
  url: "https://mdsygs.cn/",
  description: "开云体育官方正版APP下载平台，支持iOS与Android系统，提供海量体育赛事直播、实时赔率、安全存取款及专属会员福利。",
  potentialAction: {
    "@type": "SearchAction",
    target: "https://mdsygs.cn/search?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "开云体育",
  url: "https://mdsygs.cn/",
  description: "全球领先的在线体育娱乐平台，提供安全专业的体育赛事投注服务。",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "400-888-9999",
    contactType: "customer service",
    email: "service@kaiyunsports.com",
    availableLanguage: ["Chinese"],
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqData.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <SEOHead
        title="开云体育下载 - 官方正版APP免费安装 | 开云体育官方下载入口"
        description="开云体育下载官方入口，支持iOS与Android系统，一键安装正版开云体育APP，畅享海量体育赛事直播、实时赔率、安全存取款及专属会员福利，立即开云体育下载体验极速投注乐趣。"
        keywords="开云体育下载,开云体育APP下载,开云体育官方下载,开云体育安卓下载,开云体育iOS下载,开云体育最新版下载,开云体育手机版下载"
        canonicalUrl="https://mdsygs.cn/"
        schemaJson={homepageSchema}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[620px] md:h-[720px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dynamic%20sports%20stadium%20atmosphere%20with%20dramatic%20warm%20golden%20and%20amber%20lighting%2C%20abstract%20artistic%20style%20with%20motion%20blur%20effects%2C%20professional%20athletes%20silhouettes%20in%20action%2C%20stadium%20lights%20creating%20dramatic%20rays%2C%20dark%20rich%20background%20with%20warm%20accents%2C%20cinematic%20composition%2C%20epic%20scale%2C%20atmospheric%20and%20energetic%20mood&width=1920&height=1080&seq=hero-kaiyun-01&orientation=landscape"
            alt="开云体育下载 - 官方正版APP"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 md:mb-6 leading-tight">
              {heroData.title}
            </h1>
            <p className="text-sm md:text-lg text-white/80 max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed">
              {heroData.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#download"
                className="px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-base font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all duration-300 hover:scale-105"
              >
                <i className="ri-download-2-line mr-2"></i>
                {heroData.ctaText}
              </a>
              <Link
                to="/about"
                className="px-8 py-3.5 rounded-full border-2 border-white/40 text-white text-base font-medium whitespace-nowrap cursor-pointer hover:bg-white/10 hover:border-white/60 transition-all duration-300"
              >
                {heroData.secondaryCtaText}
              </Link>
            </div>

            <div className="mt-8 md:mt-10 flex flex-wrap items-center justify-center gap-3">
              <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-xs">
                <i className="ri-apple-line"></i>
                {heroData.iosBadge}
              </span>
              <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-xs">
                <i className="ri-android-line"></i>
                {heroData.androidBadge}
              </span>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <a href="#features" className="text-white/60 cursor-pointer hover:text-white transition-colors">
              <i className="ri-arrow-down-s-line text-2xl"></i>
            </a>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="w-full py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                为什么选择<strong className="text-primary-600">开云体育</strong>
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                六大核心优势，为您提供极致的体育投注体验
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuresData.map((feature, index) => (
                <div
                  key={index}
                  className="group p-6 md:p-7 rounded-xl bg-background-100 border border-background-200/60 cursor-pointer hover:border-primary-300 transition-all duration-300 hover:translate-y-[-4px]"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-300">
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                热门赛事分类
              </h2>
              <p className="text-sm text-foreground-600">
                覆盖全球主流体育项目，总有您感兴趣的赛事
              </p>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {categoriesData.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category?tab=${cat.id}`}
                  className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/60 cursor-pointer hover:border-primary-300 hover:bg-background-50 transition-all duration-300"
                >
                  <div className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full ${
                    cat.color === "primary" ? "bg-primary-100 text-primary-600" :
                    cat.color === "accent" ? "bg-accent-100 text-accent-600" :
                    "bg-secondary-100 text-secondary-600"
                  } group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-500">
                    {cat.count}篇文章
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Articles Section */}
        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 md:mb-14 gap-3">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  热门资讯
                </h2>
                <p className="text-sm text-foreground-600">
                  最新体育赛事动态与专业分析
                </p>
              </div>
              <Link
                to="/category"
                className="flex items-center gap-1 text-sm font-medium text-primary-600 cursor-pointer hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {hotArticlesData.slice(0, 6).map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group rounded-xl bg-background-100 border border-background-200/60 overflow-hidden cursor-pointer hover:border-primary-300 transition-all duration-300"
                >
                  <div className="relative w-full h-48 md:h-52 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-full bg-primary-500/90 text-background-50 text-xs font-medium">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-2">
                      {article.summary}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{article.date}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {article.views.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Download Guide Section */}
        <section id="download" className="w-full py-16 md:py-20 bg-background-900">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">
                如何下载<strong className="text-primary-400">开云体育APP</strong>
              </h2>
              <p className="text-sm md:text-base text-background-400 max-w-2xl mx-auto">
                四步轻松完成开云体育下载安装，立即开启精彩体育之旅
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {downloadStepsData.map((step) => (
                <div
                  key={step.step}
                  className="relative p-6 rounded-xl bg-background-800 border border-background-700 text-center"
                >
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-500 text-background-50 mb-4">
                    <i className={`${step.icon} text-xl`}></i>
                  </div>
                  <div className="absolute -top-3 -left-3 w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-sm font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-base font-heading font-semibold text-background-50 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-background-400 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-12 md:mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#download"
                className="px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-base font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all duration-300 flex items-center gap-2"
              >
                <i className="ri-apple-line text-lg"></i>
                iOS版下载
              </a>
              <a
                href="#download"
                className="px-8 py-3.5 rounded-full bg-accent-500 text-background-950 text-base font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-all duration-300 flex items-center gap-2"
              >
                <i className="ri-android-line text-lg"></i>
                Android版下载
              </a>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">
                关于开云体育下载与使用的常见疑问解答
              </p>
            </div>

            <div className="space-y-3">
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  className="rounded-xl bg-background-50 border border-background-200/60 overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="text-sm md:text-base font-heading font-semibold text-foreground-900 pr-4">
                      {faq.question}
                    </span>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full transition-transform duration-300 flex-shrink-0 ${
                      openFaq === index ? "bg-primary-500 text-background-50 rotate-45" : "bg-background-200 text-foreground-500"
                    }`}>
                      <i className="ri-add-line text-sm"></i>
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      openFaq === index ? "max-h-96 pb-5" : "max-h-0"
                    }`}
                  >
                    <p className="px-5 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full py-14 md:py-18 bg-primary-500">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">
              准备好开始了吗？
            </h2>
            <p className="text-sm md:text-base text-background-50/80 mb-8">
              立即下载开云体育APP，畅享海量赛事直播、实时赔率与专属会员福利
            </p>
            <a
              href="#download"
              className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-background-50 text-primary-600 text-base font-bold whitespace-nowrap cursor-pointer hover:bg-background-100 transition-all duration-300 hover:scale-105"
            >
              <i className="ri-download-2-line text-lg"></i>
              免费下载开云体育APP
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}